import { useEffect } from 'react';

const SELECT_MENU_SELECTOR = '.hoi-poi-select__menu';
const SELECT_CONTROL_SELECTOR = '.hoi-poi-select__control';
const SELECT_INPUT_SELECTOR = '.hoi-poi-select__control input';

const isTargetInsideSelectMenu = (target) => {
    if (!(target instanceof Node)) return false;
    return Array.from(document.querySelectorAll(SELECT_MENU_SELECTOR)).some((menu) =>
        menu.contains(target),
    );
};

const isTargetInsideCellSelectControl = (target, container) => {
    if (!(target instanceof Node) || !container) return false;
    const control = target.closest?.(SELECT_CONTROL_SELECTOR);
    return Boolean(control && container.contains(control));
};

/** Keep menu open only while interacting with the portaled menu or this cell's select control. */
const shouldKeepMenuOpen = (target, container) => {
    if (!(target instanceof Node) || !container) return false;
    if (isTargetInsideSelectMenu(target)) return true;
    return isTargetInsideCellSelectControl(target, container);
};

const closeSelectMenu = (container, select) => {
    select?.closeMenu?.();
    select?.blur?.();

    const input = container?.querySelector?.(SELECT_INPUT_SELECTOR);
    if (input instanceof HTMLElement) {
        input.blur();
    }
};

/** Closes react-select when clicking outside the cell (ag-grid does not always blur on its own). */
export const useBlurSelectOnOutsidePress = (containerRef, selectRef, enabled = true) => {
    useEffect(() => {
        if (!enabled) return undefined;

        const handlePointerDown = (event) => {
            const container = containerRef.current;
            const select = selectRef.current;
            if (!container || !select) return;

            const target = event.target;
            if (shouldKeepMenuOpen(target, container)) return;

            closeSelectMenu(container, select);

            // ag-grid may refocus the cell on the same click; close again next frame unless focus moved back in.
            requestAnimationFrame(() => {
                const currentContainer = containerRef.current;
                const currentSelect = selectRef.current;
                if (!currentContainer || !currentSelect) return;

                const activeTarget = document.activeElement;
                if (
                    shouldKeepMenuOpen(activeTarget, currentContainer) ||
                    shouldKeepMenuOpen(event.target, currentContainer)
                ) {
                    return;
                }

                closeSelectMenu(currentContainer, currentSelect);
            });
        };

        document.addEventListener('mousedown', handlePointerDown, true);
        return () => document.removeEventListener('mousedown', handlePointerDown, true);
    }, [containerRef, enabled, selectRef]);
};
